import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import {
  getAuth,
  signInWithEmailAndPassword as fbSignInWithEmail,
  createUserWithEmailAndPassword as fbCreateUser,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  RecaptchaVerifier,
  PhoneAuthProvider,
  linkWithCredential,
  type Auth,
  type UserCredential,
  FacebookAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import type {
  AuthConfig,
  AuthState,
  AuthStateListener,
  LoginResponse,
  UserProfile,
  VerifyEmailResponse,
  VerifyPhoneResponse,
} from './types';
import { createTokenStore, type TokenStore } from './token-storage';

export class AuthClient {
  private readonly firebaseApp: FirebaseApp;
  private readonly firebaseAuth: Auth;
  private readonly store: TokenStore;
  private readonly apiBaseUrl: string;
  private readonly autoRefresh: boolean;
  private readonly refreshBufferSeconds: number;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshPromise: Promise<void> | null = null;
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private phoneVerificationId: string | null = null;
  private listeners: Set<AuthStateListener> = new Set();
  private currentState: AuthState = {
    isAuthenticated: false,
    isLoading: true,
    user: null,
    accessToken: null,
  };

  constructor(config: AuthConfig) {
    this.firebaseApp = initializeApp(config.firebase);
    // App Check must be initialized before any other Firebase service is used.
    this.initAppCheck(config.appCheckRecaptchaSiteKey);
    this.firebaseAuth = getAuth(this.firebaseApp);
    this.store = createTokenStore(config.storage);
    this.apiBaseUrl = config.apiBaseUrl.replace(/\/$/, '');
    this.autoRefresh = config.autoRefresh ?? true;
    this.refreshBufferSeconds = config.refreshBufferSeconds ?? 60;

    // Restore persisted session
    this.restoreSession();
  }

  /**
   * Initialize Firebase App Check with reCAPTCHA v3, so Firebase Auth (sign-in / sign-up)
   * and any App-Check-enforced backend can attest that requests come from the genuine app.
   * Mirrors the Crowncoins web app, which shares this Firebase project + web app registration.
   *
   * Browser-only and gated on a site key: when the key is absent (or under Node/SSR/tests),
   * App Check is skipped so auth still works. Whether a missing token actually blocks a
   * request is decided by App Check enforcement in the Firebase console — so this is safe to
   * ship ahead of the enforcement rollout. Failure is swallowed: breaking client construction
   * would take auth down entirely, and an unenforced project simply ignores the missing token.
   */
  private initAppCheck(recaptchaV3SiteKey?: string): void {
    if (typeof window === 'undefined' || !recaptchaV3SiteKey) return;
    try {
      initializeAppCheck(this.firebaseApp, {
        provider: new ReCaptchaV3Provider(recaptchaV3SiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch {
      // Non-fatal: App Check only blocks once enforced in console; don't break auth.
    }
  }

  // ─── Firebase Auth Methods ────────────────────────────────────────────

  /**
   * Sign in with email + password via Firebase, then authenticate with predictions-api.
   */
  async signInWithEmailAndPassword(email: string, password: string): Promise<LoginResponse> {
    const credential = await fbSignInWithEmail(this.firebaseAuth, email, password);
    return this.authenticateWithBackend(credential);
  }

  /**
   * Create a new Firebase account, then register + authenticate with predictions-api.
   */
  async signUp(email: string, password: string): Promise<LoginResponse> {
    const credential = await fbCreateUser(this.firebaseAuth, email, password);
    return this.authenticateWithBackend(credential);
  }

  /**
   * Sign in with Google popup via Firebase, then authenticate with predictions-api.
   */
  async signInWithGoogle(): Promise<LoginResponse> {
    const provider = new GoogleAuthProvider();
    const credential = await signInWithPopup(this.firebaseAuth, provider);
    return this.authenticateWithBackend(credential);
  }

  /**
   * Sign in with Facebook popup via Firebase, then authenticate with predictions-api.
   */
  async signInWithFacebook(): Promise<LoginResponse> {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    const credential = await signInWithPopup(this.firebaseAuth, provider);
    return this.authenticateWithBackend(credential);
  }

  /**
   * Sign in with Apple popup via Firebase, then authenticate with predictions-api.
   */
  async signInWithApple(): Promise<LoginResponse> {
    const provider = new OAuthProvider('apple.com');
    const credential = await signInWithPopup(this.firebaseAuth, provider);
    return this.authenticateWithBackend(credential);
  }

  // ─── Phone OTP Verification ───────────────────────────────────────────

  /**
   * Set up an invisible reCAPTCHA verifier on the given container element.
   * Must be called before `sendPhoneVerificationCode`.
   */
  setupRecaptcha(containerId: string): void {
    this.clearRecaptcha();
    this.recaptchaVerifier = new RecaptchaVerifier(this.firebaseAuth, containerId, { size: 'invisible' });
  }

  /**
   * Send an SMS verification code to the given phone number (E.164 format).
   * Requires `setupRecaptcha` to have been called first.
   * Returns the verificationId needed for `confirmPhoneVerificationCode`.
   */
  async sendPhoneVerificationCode(phoneNumber: string): Promise<string> {
    if (!this.recaptchaVerifier) {
      throw new Error('Call setupRecaptcha() before sending a verification code');
    }

    const provider = new PhoneAuthProvider(this.firebaseAuth);
    const verificationId = await provider.verifyPhoneNumber(phoneNumber, this.recaptchaVerifier);
    this.phoneVerificationId = verificationId;
    return verificationId;
  }

  /**
   * Confirm the SMS OTP code, link the phone to the current Firebase user,
   * and notify the backend to persist the verification.
   */
  async confirmPhoneVerificationCode(code: string): Promise<VerifyPhoneResponse> {
    if (!this.phoneVerificationId) {
      throw new Error('No pending verification — call sendPhoneVerificationCode() first');
    }

    const currentUser = this.firebaseAuth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated Firebase user — sign in before verifying phone');
    }

    // Link phone credential to existing Firebase user
    const credential = PhoneAuthProvider.credential(this.phoneVerificationId, code);
    await linkWithCredential(currentUser, credential);

    // Get fresh Firebase ID token (force refresh to include phone claim)
    const firebaseIdToken = await currentUser.getIdToken(true);

    // Notify backend
    const accessToken = this.store.getAccessToken();
    const response = await this.apiRequest<VerifyPhoneResponse>('/auth/verify-phone', {
      method: 'POST',
      body: JSON.stringify({ firebaseIdToken }),
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

    // Persist fresh profile and notify listeners so UI reflects phone + phoneVerified
    this.store.setUserProfile(JSON.stringify(response.profile));
    this.setState({
      ...this.currentState,
      user: response.profile,
    });

    this.phoneVerificationId = null;
    return response;
  }

  /**
   * Clean up the reCAPTCHA verifier and any pending verification state.
   */
  clearRecaptcha(): void {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
    this.phoneVerificationId = null;
  }

  // ─── Email Verification ───────────────────────────────────────────────

  /**
   * Send (or resend) the branded verification email via predictions-api. The backend
   * generates the Firebase action link and delivers it through SES — clicking the link
   * is still what makes Firebase mark the email verified. No-op server-side if the email
   * is already verified upstream.
   */
  async sendEmailVerification(): Promise<void> {
    await this.authedRequest('/auth/send-verification-email', { method: 'POST' });
  }

  /**
   * Poll the Firebase user until `emailVerified` flips to true (the user clicked the
   * link in their inbox). When detected, force-refresh the ID token, call the backend
   * to mirror the verification, persist the fresh profile, and notify auth-state
   * listeners. Returns an unsubscribe function so the caller can cancel the poll
   * (e.g. on component unmount).
   *
   * `callback` runs with the resolved `VerifyEmailResponse` on success — usually the
   * caller will use this to advance their UI.
   */
  watchEmailVerification(
    callback: (response: VerifyEmailResponse) => void,
    options?: { intervalMs?: number; onError?: (error: Error) => void },
  ): () => void {
    const intervalMs = options?.intervalMs ?? 3000;
    let cancelled = false;
    let inFlight = false;
    let handle: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      cancelled = true;
      if (handle !== null) {
        clearInterval(handle);
        handle = null;
      }
    };

    const tick = async () => {
      if (cancelled || inFlight) return;
      const currentUser = this.firebaseAuth.currentUser;
      if (!currentUser) {
        stop();
        return;
      }
      inFlight = true;
      try {
        await currentUser.reload();
        if (cancelled) return;
        if (currentUser.emailVerified) {
          stop();
          const response = await this.confirmEmailVerification();
          callback(response);
        }
      } catch (err) {
        options?.onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        inFlight = false;
      }
    };

    handle = setInterval(tick, intervalMs);
    // Run one immediate check — covers the case where the email was already verified before mounting.
    void tick();

    return stop;
  }

  /**
   * Force-refresh the Firebase ID token and notify the backend that this user's email
   * is verified. Updates local state and storage with the returned profile. Throws if
   * Firebase still reports the email as unverified — callers should normally use
   * `watchEmailVerification` and only call this directly when they have already
   * observed `currentUser.emailVerified === true`.
   */
  async confirmEmailVerification(): Promise<VerifyEmailResponse> {
    const currentUser = this.firebaseAuth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated Firebase user — sign in before confirming email verification');
    }
    if (!currentUser.emailVerified) {
      throw new Error('Email not yet verified in Firebase — open the verification link first');
    }

    const firebaseIdToken = await currentUser.getIdToken(true);
    const accessToken = this.store.getAccessToken();

    const response = await this.apiRequest<VerifyEmailResponse>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ firebaseIdToken }),
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

    this.store.setUserProfile(JSON.stringify(response.profile));
    this.setState({
      ...this.currentState,
      user: response.profile,
    });

    return response;
  }

  // ─── Token Management ────────────────────────────────────────────────

  /**
   * Mint a fresh access token from the Firebase session. There is no app-side
   * refresh token — Firebase maintains the long-lived session, so we force-refresh
   * the Firebase ID token and POST it to /auth/refresh for a new access token (and
   * up-to-date profile claims). Clears the session if Firebase has no current user,
   * or if the server rejects the session outright (401/403 — account gone,
   * deactivated, or locked).
   *
   * Single-flight: concurrent callers (the proactive expiry timer plus any number
   * of reactive 401 retries) share one in-flight mint instead of stampeding the api.
   */
  async refresh(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.doRefresh().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  private async doRefresh(): Promise<void> {
    const currentUser = this.firebaseAuth.currentUser;
    if (!currentUser) {
      this.clearSession();
      throw new Error('No Firebase session available');
    }

    const firebaseIdToken = await currentUser.getIdToken(true);
    try {
      await this.mintAccessToken('/auth/refresh', firebaseIdToken);
    } catch (error) {
      const status = (error as { status?: number }).status;
      // Older api without /auth/refresh — fall back to re-login so a UI deployed
      // ahead of the api keeps its sessions alive. Remove once /auth/refresh is
      // live everywhere.
      if (status === 404) {
        await this.mintAccessToken('/auth/login', firebaseIdToken);
        return;
      }
      // The server rejected the session itself (unknown account, deactivated,
      // locked) — the stored token is dead weight; clear so the app shows the
      // signed-out state instead of retrying forever.
      if (status === 401 || status === 403) {
        this.clearSession();
      }
      throw error;
    }
  }

  /**
   * Replace the stored access token with a freshly-minted one — e.g. the token
   * returned by `/auth/onboarding` after address verification flips the
   * `isAddressVerified` claim. Swapping it here means verification-gated routes
   * pass on the very next request, without waiting for the scheduled refresh.
   *
   * The auto-refresh timer is rescheduled to the new token's expiry and listeners
   * are notified so consumers re-read `accessToken` from auth state.
   */
  applyAccessToken(accessToken: string): void {
    this.store.setAccessToken(accessToken);
    this.scheduleRefresh(accessToken);
    this.setState({
      ...this.currentState,
      accessToken,
    });
  }

  /**
   * Logout — revoke the Firebase session server-side (all devices), sign out of
   * Firebase locally, and clear local state.
   */
  async logout(): Promise<void> {
    try {
      await this.authedRequest('/auth/logout', { method: 'POST' });
    } catch {
      // Best-effort; clear local state regardless
    }

    await fbSignOut(this.firebaseAuth);
    this.clearSession();
  }

  // ─── State Accessors ─────────────────────────────────────────────────

  /** Current access token (or null). */
  getAccessToken(): string | null {
    return this.store.getAccessToken();
  }

  /** Current user profile (or null). */
  getUser(): UserProfile | null {
    return this.currentState.user;
  }

  /** Whether the user is currently authenticated. */
  isAuthenticated(): boolean {
    return this.currentState.isAuthenticated;
  }

  /** Current auth state snapshot. */
  getState(): AuthState {
    return { ...this.currentState };
  }

  /**
   * Subscribe to auth state changes. Returns an unsubscribe function.
   */
  onAuthStateChange(listener: AuthStateListener): () => void {
    this.listeners.add(listener);
    // Emit current state immediately
    listener({ ...this.currentState });
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Returns a fetch-compatible headers object with the Authorization header set.
   * Useful for making authenticated API calls.
   */
  getAuthHeaders(): Record<string, string> {
    const token = this.store.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // ─── Internals ───────────────────────────────────────────────────────

  private async authenticateWithBackend(credential: UserCredential): Promise<LoginResponse> {
    const firebaseIdToken = await credential.user.getIdToken();
    return this.loginWithFirebaseToken(firebaseIdToken);
  }

  private async loginWithFirebaseToken(firebaseIdToken: string): Promise<LoginResponse> {
    return this.mintAccessToken('/auth/login', firebaseIdToken);
  }

  /**
   * POST a Firebase ID token to a token-minting endpoint (/auth/login or
   * /auth/refresh — identical response contract), persist the session, and
   * schedule the proactive refresh.
   */
  private async mintAccessToken(path: '/auth/login' | '/auth/refresh', firebaseIdToken: string): Promise<LoginResponse> {
    const response = await this.apiRequest<LoginResponse>(path, {
      method: 'POST',
      body: JSON.stringify({ firebaseIdToken }),
    });

    this.store.setAccessToken(response.accessToken);
    this.store.setUserProfile(JSON.stringify(response.profile));

    this.scheduleRefresh(response.accessToken);

    this.setState({
      isAuthenticated: true,
      isLoading: false,
      user: response.profile,
      accessToken: response.accessToken,
    });

    return response;
  }

  private restoreSession(): void {
    const accessToken = this.store.getAccessToken();
    const profileJson = this.store.getUserProfile();

    if (!accessToken || !profileJson) {
      this.setState({ isAuthenticated: false, isLoading: true, user: null, accessToken: null });
      this.attemptFirebaseReauth();
      return;
    }

    const user: UserProfile = JSON.parse(profileJson);

    // Check if access token is expired
    if (this.isTokenExpired(accessToken)) {
      // Attempt silent re-mint from the Firebase session. refresh() re-calls
      // /auth/login, which sets the authenticated state with the fresh profile.
      this.setState({ isAuthenticated: false, isLoading: true, user, accessToken: null });
      this.refresh().catch(() => {
        this.clearSession();
      });
      return;
    }

    this.scheduleRefresh(accessToken);
    this.setState({ isAuthenticated: true, isLoading: false, user, accessToken });
  }

  private scheduleRefresh(accessToken: string): void {
    if (!this.autoRefresh) return;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const expiresIn = this.getTokenExpiresIn(accessToken);
    if (expiresIn <= 0) return;

    const refreshIn = Math.max((expiresIn - this.refreshBufferSeconds) * 1000, 0);

    this.refreshTimer = setTimeout(() => {
      this.refresh().catch(() => {
        // Token refresh failed — user will need to re-authenticate on next API call
      });
    }, refreshIn);
  }

  private async attemptFirebaseReauth(): Promise<void> {
    try {
      await this.firebaseAuth.authStateReady();
      const firebaseUser = this.firebaseAuth.currentUser;

      if (!firebaseUser) {
        this.setState({ isAuthenticated: false, isLoading: false, user: null, accessToken: null });
        return;
      }

      const firebaseIdToken = await firebaseUser.getIdToken();
      await this.loginWithFirebaseToken(firebaseIdToken);
    } catch {
      this.clearSession();
    }
  }

  private clearSession(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.store.clear();
    this.setState({ isAuthenticated: false, isLoading: false, user: null, accessToken: null });
  }

  private setState(state: AuthState): void {
    this.currentState = state;
    for (const listener of this.listeners) {
      try {
        listener({ ...state });
      } catch {
        // Don't let listener errors break the auth flow
      }
    }
  }

  private getTokenExpiresIn(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp - Math.floor(Date.now() / 1000);
    } catch {
      return 0;
    }
  }

  private isTokenExpired(token: string): boolean {
    return this.getTokenExpiresIn(token) <= 0;
  }

  private async authedRequest<T>(path: string, init: RequestInit, isRetry = false): Promise<T> {
    const accessToken = this.store.getAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated');
    }
    try {
      return await this.apiRequest<T>(path, {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch (error) {
      // Expired/invalid access token — mint a fresh one from the Firebase session
      // and replay once. refresh() uses apiRequest directly, so this can't recurse.
      if ((error as { status?: number }).status === 401 && !isRetry) {
        await this.refresh();
        return this.authedRequest<T>(path, init, true);
      }
      throw error;
    }
  }

  private async apiRequest<T>(path: string, init: RequestInit): Promise<T> {
    const url = `${this.apiBaseUrl}${path}`;
    const hasBody = init.body !== undefined && init.body !== null;
    const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
    if (hasBody && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(url, { ...init, headers });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}) as Record<string, unknown>);
      const message = (body as Record<string, unknown>).message || `Auth API error: ${response.status}`;
      const error = new Error(String(message)) as Error & { status: number; body: unknown };
      error.status = response.status;
      error.body = body;
      throw error;
    }

    // 204 No Content
    if (response.status === 204) return undefined as unknown as T;

    return response.json() as Promise<T>;
  }
}
